'use client';

import { createClient } from '@/lib/supabase/client';

export type NotificationType = 'registration' | 'assignment' | 'message' | 'live_session';

export interface AppNotification {
  id: string;
  recipientId: string;
  recipientRole: 'student' | 'lecturer' | 'admin';
  type: NotificationType;
  title: string;
  body: string;
  courseId?: string;
  referenceId?: string;
  isRead: boolean;
  createdAt: string;
}

function dbToNotification(row: any): AppNotification {
  return {
    id: row.id,
    recipientId: row.recipient_id,
    recipientRole: row.recipient_role,
    type: row.type,
    title: row.title,
    body: row.body,
    courseId: row.course_id || undefined,
    referenceId: row.reference_id || undefined,
    isRead: row.is_read,
    createdAt: row.created_at,
  };
}

export const notificationService = {
  async getForUser(recipientId: string): Promise<AppNotification[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('recipient_id', recipientId)
        .order('created_at', { ascending: false })
        .limit(50);
      if (error) return [];
      return (data || []).map(dbToNotification);
    } catch {
      return [];
    }
  },

  async markRead(notificationId: string): Promise<void> {
    const supabase = createClient();
    try {
      await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('id', notificationId);
    } catch { /* ignore */ }
  },

  async markAllRead(recipientId: string): Promise<void> {
    const supabase = createClient();
    try {
      await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('recipient_id', recipientId)
        .eq('is_read', false);
    } catch { /* ignore */ }
  },

  async create(notification: Omit<AppNotification, 'id' | 'createdAt' | 'isRead'>): Promise<void> {
    const supabase = createClient();
    try {
      await supabase.from('notifications').insert({
        recipient_id: notification.recipientId,
        recipient_role: notification.recipientRole,
        type: notification.type,
        title: notification.title,
        body: notification.body,
        course_id: notification.courseId || null,
        reference_id: notification.referenceId || null,
        is_read: false,
      });
    } catch { /* ignore */ }
  },

  subscribeToNotifications(
    recipientId: string,
    onNotification: (n: AppNotification) => void
  ) {
    const supabase = createClient();
    // Use a unique channel name per subscription instance to avoid conflicts
    const channelName = `notifications:${recipientId}:${Date.now()}`;
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `recipient_id=eq.${recipientId}`,
        },
        (payload) => {
          if (payload.new) onNotification(dbToNotification(payload.new));
        }
      )
      .subscribe();
    return channel;
  },
};
